import React, { useCallback, useMemo } from 'react';
import './token-details.scss';
import { LoadDocBtn } from '@ru-digital-assets/ui-component';
import currencyFormat from 'utils/currencyFormat';
import { getFormatedDate } from 'utils';
import { useDispatch } from 'hooks/redux';
import { SIGN_DOC_TYPE } from 'types';
import { getUcpDoc } from 'store/actions/operations-sign';
import { downloadFile, getPdfFromBase64 } from 'utils/files/downloadFile';
import { AssetBackendStatus } from 'constants/redemption';
import { setPush } from 'store/actions/app';
import TokenStatusBadge, {
  TokenStatusBadgeVariant,
} from 'components/TokenStatusBadge';

interface ITokenDetails {
  asset: any;
}

const getTokenBadgeStatus = (asset: any): [string, TokenStatusBadgeVariant] => {
  switch (asset.status) {
    case AssetBackendStatus.REDEEM_PAYMENT_SIGNED:
    case AssetBackendStatus.OWNER_PAYMENT_SIGNED:
      return asset.isIssuer
        ? ['Ожидается подтверждение погашения', 'grey']
        : ['Требуется подтвердить погашение', 'yellow'];
    case AssetBackendStatus.REDEEM_INITIALIZED:
    case AssetBackendStatus.REDEEM_PAYMENT_UPLOADED:
      return [
        'Требуется подтвердить оплату',
        asset.isIssuer ? 'yellow' : 'grey',
      ];
    case AssetBackendStatus.REDEEMED:
      return ['Погашен', 'grey'];
    default:
      return ['Активен', 'green'];
  }
};

export default function TokenDetails({ asset }: ITokenDetails) {
  const dispatch = useDispatch();

  const downloadDoc = useCallback(
    (id: number, type: SIGN_DOC_TYPE, title: string) => {
      dispatch(
        getUcpDoc({
          id,
          type,
          contentType: 'application/pdf',
        })
      )
        .then((res) => {
          const resultBlob = getPdfFromBase64(
            (res.payload as { doc: string; hash: string }).doc
          );
          downloadFile(resultBlob, title, 'application/pdf');
        })
        .catch((error) =>
          dispatch(
            setPush({
              message: 'Ошибка при загрузке файла',
              type: 'error',
            })
          )
        );
    },
    []
  );

  const decisionDoc = useMemo(() => {
    return asset.documentLinks.find((doc: any) => doc.type === 'DECISION');
  }, [asset]);

  const signedDecisionDoc = useMemo(() => {
    return asset.documentLinks.find(
      (doc: any) => doc.type === 'SIGNED_DECISION'
    );
  }, [asset]);

  const [tokenStatusText, tokenStatusVariant] = getTokenBadgeStatus(asset);

  return (
    <div className="token-details">
      <div className="token-details__token-info">
        <div className="token-details__token-info-list">
          <div>
            <p className="p p_bold mb_8">Наименование токена:</p>
            <p className="p p_font-size_24 p_font-weight_light p_color_light-blue">
              {asset.issuingInfo.tokenName}
            </p>
          </div>
          <div>
            <p className="p p_bold mb_8">Количество (шт):</p>
            <p className="p p_font-size_24 p_font-weight_light">
              {asset.actualCount}
            </p>
          </div>
          {asset.holdCount !== undefined && asset.holdCount !== 0 && (
            <div>
              <p className="p p_bold mb_8">В резерве (шт):</p>
              <p className="p p_font-size_24 p_font-weight_light">
                {asset.holdCount}
              </p>
            </div>
          )}
          {asset.lockedCount !== undefined && asset.lockedCount !== 0 && (
            <div>
              <p className="p p_bold mb_8 p_color_red">Заблокировано (шт):</p>
              <p className="p p_font-size_24 p_font-weight_light p_color_red">
                {asset.lockedCount}
              </p>
            </div>
          )}
          <div>
            <p className="p p_bold mb_8">Общая стоимость:</p>
            <p className="p p_font-size_24 p_font-weight_light">
              {currencyFormat(
                asset.actualCount * asset.issuingInfo.tokenPrice,
                true
              )}
            </p>
          </div>
        </div>
        <div className="mt_8">
          <TokenStatusBadge
            text={tokenStatusText}
            variant={tokenStatusVariant}
          />
        </div>
      </div>
      <div className="token-details__operation-details">
        <div>
          <p className="p p_font-size_12 p_color_light-grey">Эмитент:</p>
          <p className="p p_font-size_18 mb_6">{asset.issuer}</p>
          <p className="p p_font-size_12 p_color_light-grey">ИНН:</p>
          <p className="p p_font-size_18">{asset.issuerTIN}</p>
        </div>
        <div>
          <p className="p p_font-size_12 p_color_light-grey">
            Текущий Обладатель:
          </p>
          <p className="p p_font-size_18 mb_6">{asset.owner}</p>
          <p className="p p_font-size_12 p_color_light-grey">ИНН:</p>
          <p className="p p_font-size_18">{asset.ownerTIN}</p>
        </div>
        <div>
          <p className="p p_font-size_12 p_color_light-grey">Дата выпуска:</p>
          <p className="p p_font-size_18 mb_6">
            {getFormatedDate(asset.issuingInfo.initialDate, 'dd MMMM yyyy')}
          </p>
          <p className="p p_font-size_12 p_color_light-grey">
            Дата исполнения обязательств:
          </p>
          <p className="p p_font-size_18">
            {getFormatedDate(asset.issuingInfo.expiryDate, 'dd MMMM yyyy')}
          </p>
        </div>
      </div>
      <div className="token-details__documents">
        <p className="p p_bold mb_24">Документы к выпуску:</p>
        <div className="mb_16">
          <LoadDocBtn
            title="Решение о выпуске"
            extension={decisionDoc ? '.PDF' : undefined}
            onSpecDownload={
              decisionDoc
                ? () =>
                    downloadDoc(
                      decisionDoc.docId,
                      decisionDoc.type,
                      decisionDoc.name
                    )
                : undefined
            }
            secondExtension={signedDecisionDoc ? '.SIG' : undefined}
            onSecondSpecDownload={
              signedDecisionDoc
                ? () =>
                    downloadDoc(
                      signedDecisionDoc.docId,
                      signedDecisionDoc.type,
                      signedDecisionDoc.name
                    )
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}
